import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

/** Matches Supabase packages/ui SIZE_VARIANTS + IconContainerVariants icon scale. */
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-normal transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-50 disabled:cursor-not-allowed [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "border border-solid [background:var(--btn-default-bg)] [color:var(--btn-default-fg)] [border-color:var(--btn-default-border)] hover:[background:var(--btn-default-hover)]",
        destructive:
          "border border-solid [background:var(--btn-destructive-bg)] [color:var(--btn-destructive-fg)] [border-color:var(--btn-destructive-border)] hover:[background:var(--btn-destructive-hover)]",
        outline:
          "border border-border/60 bg-background shadow hover:bg-foreground/10 hover:text-foreground",
        secondary:
          "bg-secondary text-secondary-foreground shadow hover:bg-secondary/80",
        ghost: "hover:bg-foreground/10 hover:text-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        tiny: "h-[26px] px-2.5 py-1 text-xs [&_svg]:size-3.5",
        /** Supabase SIZE_VARIANTS.small (34px). */
        small: "h-[34px] px-3 py-2 text-sm leading-4 [&_svg]:size-[18px]",
        sm: "h-[34px] px-3 py-2 text-sm leading-4 [&_svg]:size-[18px]",
        default: "h-[34px] px-3 py-2 text-sm leading-4 [&_svg]:size-[18px]",
        md: "h-[38px] px-4 py-2 text-sm [&_svg]:size-5",
        lg: "h-[42px] px-4 py-2 text-base [&_svg]:size-5",
        xlarge: "h-[50px] px-6 py-3 text-base [&_svg]:size-6",
        icon: "h-[34px] w-[34px] [&_svg]:size-[18px]",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "tiny",
    },
  },
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  ref?: React.Ref<HTMLButtonElement>;
}

function Button({
  className,
  variant,
  size,
  asChild = false,
  ref,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : "button";
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props}
    />
  );
}

export { Button, buttonVariants };
